import { formatDate } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { RosterService } from '../../../pages/pages/roster/roster.service';
import { AppService } from '../../../app.service';

interface CalendarCell {
  date: Date;
}

@Component({
  selector: 'gtapp-gt-calendar',
  standalone: false,
  templateUrl: './gt-calendar.component.html',
  styleUrl: './gt-calendar.component.scss',
})
export class GtCalendarComponent implements OnInit {
  constructor(
    private rosterService: RosterService,
    private appService: AppService,
  ) {}

  @Input() shownFrom: 'requestLeave' = 'requestLeave';
  @Output() onSelectDate = new EventEmitter();
  weekDays: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  monthNames: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  today = new Date();
  currentDate = signal<Date>(new Date());

  selectedDates = new Set();

  currentMonthName = computed(
    () => this.monthNames[this.currentDate().getMonth()],
  );
  currentYear = computed(() => this.currentDate().getFullYear());

  calendarCells = computed<CalendarCell[]>(() => {
    const cells: CalendarCell[] = [];
    const date = this.currentDate();
    const year = date.getFullYear();
    const month = date.getMonth();

    // Find first day of month index allocation (0 = Sunday, 1 = Monday...)
    const firstDayIndex = new Date(year, month, 1).getDay();

    // Total days inside current active targeted target month
    const totalDaysInMonth = new Date(year, month + 1, 0).getDate();

    // Total calculation buffer constraints from preceding padding slots
    const totalDaysInPrevMonth = new Date(year, month, 0).getDate();

    // 1. Generate padding cells from the previous month
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = totalDaysInPrevMonth - i;
      const cellDate = new Date(year, month - 1, dayNum);
      cells.push({ date: cellDate });
    }

    // 2. Generate actual active current targeted month tracking numbers
    for (let dayNum = 1; dayNum <= totalDaysInMonth; dayNum++) {
      const cellDate = new Date(year, month, dayNum);
      cells.push({ date: cellDate });
    }

    // 3. Generate trailing buffer overflow segments to fill a consistent 6-row (42 cells) block grid view
    const totalGridCellsNeeded = 42;
    const currentFilledCount = cells.length;
    const nextMonthPaddingNeeded = totalGridCellsNeeded - currentFilledCount;

    for (let dayNum = 1; dayNum <= nextMonthPaddingNeeded; dayNum++) {
      const cellDate = new Date(year, month + 1, dayNum);
      cells.push({ date: cellDate });
    }

    return cells;
  });

  subscriberUserId =
    this.appService.getUserData()?.subscriber?.subscriber_user_id;

  ngOnInit(): void {
    switch (this.shownFrom) {
      case 'requestLeave':
        this.getUsersLeaveList();
        break;
    }
  }

  previousMonth(): void {
    const current = this.currentDate();
    this.currentDate.set(
      new Date(current.getFullYear(), current.getMonth() - 1, 1),
    );
  }

  nextMonth(): void {
    const current = this.currentDate();
    this.currentDate.set(
      new Date(current.getFullYear(), current.getMonth() + 1, 1),
    );
  }

  goToToday(): void {
    const today = new Date();
    this.currentDate.set(new Date(today.getFullYear(), today.getMonth(), 1));
    this.selectDate(today);
  }

  selectDate(date: Date): void {
    const dateStr = formatDate(date, 'd MMM y', 'en_US');

    if (this.selectedDates.has(dateStr)) {
      this.selectedDates.delete(dateStr);
    } else {
      this.selectedDates.add(dateStr);
    }
    const selectedDates = [...this.selectedDates]?.map(
      (item: any) => new Date(item),
    );
    this.onSelectDate.emit(selectedDates);
  }
  getUsersLeaveList(nextCursor?: string) {
    let params: any = {
      rows: 5,
      start_day: formatDate(
        this.calendarCells()[0]?.date,
        'yyyy-MM-dd',
        'en_US',
      ),
      end_day: formatDate(
        this.calendarCells()[this.calendarCells()?.length - 1]?.date,
        'yyyy-MM-dd',
        'en_US',
      ),

      cursor_pagination: 1,
      // status_ids: JSON.stringify([1]),
      user_id: this.subscriberUserId,
    };
    if (nextCursor) params.next_cursor = nextCursor;
    this.rosterService.getAllLeaves(params).subscribe((response: any) => {
      const _selectedDates = response?.data?.map((item: any) =>
        formatDate(item?.leave_date, 'd MMM y', 'en_US'),
      );
      this.selectedDates = new Set([...this.selectedDates, ..._selectedDates]);

      if (response?.next_cursor) this.getUsersLeaveList(response?.next_cursor);
    });
  }
}
